"use client";

import { Button } from "@package/subframe/components/Button";
import { IconWithBackground } from "@package/subframe/components/IconWithBackground";
import { IconWithLogoBackground } from "@package/subframe/components/IconWithLogoBackground";
import { LinkButton } from "@package/subframe/components/LinkButton";
import { TextField } from "@package/subframe/components/TextField";
import {
  FeatherCheck,
  FeatherEye,
  FeatherEyeOff,
  FeatherLock,
} from "@subframe/core";
import { useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { toast } from "sonner";

import { useAuth } from "@/contexts/AuthContext";
import { useChangePasswordCommandControllerChangePassword } from "@/generated/auth/auth";
import { signOut } from "@/lib/auth";
import { signIn } from "@/lib/firebase-auth";

export function SetPasswordForm() {
  const navigate = useNavigate();
  const { refreshSession, user } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const changePassword = useChangePasswordCommandControllerChangePassword();

  const passwordRequirements = [
    { text: "At least 8 characters", met: password.length >= 8 },
    { text: "One uppercase letter", met: /[A-Z]/.test(password) },
    { text: "One number", met: /\d/.test(password) },
  ];

  const passwordsMatch = password === confirmPassword && password.length > 0;
  const allRequirementsMet = passwordRequirements.every((req) => req.met);

  const handleSetPassword = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!allRequirementsMet || !passwordsMatch) {
      toast.error("Please meet all password requirements");
      return;
    }

    changePassword.mutate(
      { data: { newPassword: password } },
      {
        onSuccess: async () => {
          try {
            // Re-login silently with the new password
            // (Firebase Admin SDK revokes refresh tokens on password change)
            if (user?.email) {
              await signIn(user.email, password);
            }
            // Refresh session to get updated user data
            await refreshSession();
            toast.success("Password set successfully");
            // Deliberately not the index route: "/" is absent from the nursery
            // staff allowlist, so restricted staff bounce to /no-access before
            // it can route them. /dashboard is allowlisted, and the dashboard
            // forwards nursery staff on to the Workspace.
            navigate({ to: "/dashboard", replace: true });
          } catch (error) {
            console.error("Failed to re-authenticate:", error);
            toast.error("Password set but please sign in again");
            await signOut();
            navigate({ to: "/login", replace: true });
          }
        },
        onError: () => {
          toast.error("Failed to set password");
        },
      },
    );
  };

  const handleSignOut = () => {
    signOut();
    navigate({ to: "/login", replace: true });
  };

  return (
    <div className="flex h-full min-h-screen w-full flex-col items-center justify-center bg-white px-6 py-12">
      <div className="flex w-full max-w-[384px] flex-col items-center gap-6">
        {/* Lock Icon */}
        <IconWithLogoBackground
          variant="neutral"
          size="x-large"
          icon={<FeatherLock />}
        />

        {/* Header */}
        <div className="flex w-full flex-col items-center gap-2">
          <span className="text-heading-1 font-heading-1 text-neutral-900">
            Set your password
          </span>
          <span className="text-body font-body text-subtext-color text-center">
            Please create a secure password for your account
          </span>
        </div>

        {/* Form */}
        <form
          onSubmit={handleSetPassword}
          className="flex w-full flex-col items-start gap-6"
        >
          <TextField
            className="h-auto w-full flex-none"
            size="large"
            label="New password"
            iconRight={
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="text-subtext-color hover:text-default-font"
              >
                {showPassword ? <FeatherEyeOff /> : <FeatherEye />}
              </button>
            }
          >
            <TextField.Input
              type={showPassword ? "text" : "password"}
              placeholder="Enter new password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={changePassword.isPending}
            />
          </TextField>

          <TextField
            className="h-auto w-full flex-none"
            size="large"
            label="Confirm password"
            error={confirmPassword.length > 0 && !passwordsMatch}
            helpText={
              confirmPassword.length > 0
                ? passwordsMatch
                  ? ""
                  : "Passwords do not match"
                : ""
            }
            iconRight={
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="text-subtext-color hover:text-default-font"
              >
                {showConfirmPassword ? <FeatherEyeOff /> : <FeatherEye />}
              </button>
            }
          >
            <TextField.Input
              type={showConfirmPassword ? "text" : "password"}
              placeholder="Confirm new password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              disabled={changePassword.isPending}
            />
          </TextField>

          {/* Password Requirements */}
          <div className="flex w-full flex-col items-start gap-3">
            <span className="text-body-bold font-body-bold text-default-font">
              Password must contain:
            </span>
            <div className="flex w-full flex-col items-start gap-2">
              {passwordRequirements.map((requirement, index) => (
                <div key={index} className="flex items-center gap-2">
                  <IconWithBackground
                    variant={requirement.met ? "success" : "neutral"}
                    size="x-small"
                    icon={<FeatherCheck />}
                    square={false}
                  />
                  <span
                    className={`text-body font-body ${
                      requirement.met
                        ? "text-success-700"
                        : "text-subtext-color"
                    }`}
                  >
                    {requirement.text}
                  </span>
                </div>
              ))}
            </div>
          </div>

          <Button
            className="h-12 w-full flex-none"
            size="large"
            type="submit"
            disabled={
              !allRequirementsMet || !passwordsMatch || changePassword.isPending
            }
          >
            {changePassword.isPending ? "Setting password..." : "Set password"}
          </Button>
        </form>

        {/* Sign out link */}
        <LinkButton variant="neutral" size="medium" onClick={handleSignOut}>
          Sign out
        </LinkButton>

        {/* Terms and Privacy */}
        <div className="flex flex-wrap items-center justify-center gap-1 mobile:h-auto mobile:w-full mobile:flex-none mobile:flex-col mobile:flex-nowrap mobile:gap-1">
          <span className="text-caption font-caption text-subtext-color mobile:line-clamp-none mobile:whitespace-pre-wrap mobile:break-normal mobile:text-center">
            By clicking &quot;Set password&quot; you agree to the{" "}
          </span>
          <div className="flex flex-wrap items-center justify-center gap-1 mobile:h-auto mobile:w-full mobile:flex-none">
            <LinkButton variant="brand" size="small">
              Terms of service
            </LinkButton>
            <span className="text-caption font-caption text-subtext-color">
              and acknowledge the
            </span>
            <LinkButton variant="brand" size="small">
              Privacy notice
            </LinkButton>
          </div>
        </div>
      </div>
    </div>
  );
}
